import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  Copy,
  Download,
  Folder,
  FolderInput,
  HardDrive,
  Link as LinkIcon,
  List,
  MoreHorizontal,
  Pencil,
  Play,
  Share2,
  Star,
  Tag,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { toastify } from "../Toast";
import ShareCard from "../../shades/cards/shareCard";
import services from "../../API";
import "./FileDetail.scss";

const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"];
const videoExtensions = ["mp4", "mov", "avi", "webm", "mkv"];

const getExtension = (fileName = "") => {
  const extension = fileName.split(".").pop();
  return extension && extension !== fileName ? extension.toLowerCase() : "file";
};

const getFileStyle = (extension) => {
  const type = extension.toLowerCase();
  if (type === "pdf") return { tone: "pdf", label: "PDF Document", badge: "PDF" };
  if (["doc", "docx", "rtf"].includes(type)) return { tone: "document", label: "Word Document", badge: type.toUpperCase() };
  if (["xls", "xlsx", "csv"].includes(type)) return { tone: "sheet", label: "Excel Spreadsheet", badge: type.toUpperCase() };
  if (["ppt", "pptx", "key"].includes(type)) return { tone: "presentation", label: "PowerPoint Presentation", badge: type.toUpperCase() };
  if (imageExtensions.includes(type)) return { tone: "image", label: "Image", badge: type.toUpperCase() };
  if (videoExtensions.includes(type)) return { tone: "video", label: "Video", badge: type.toUpperCase() };
  if (["mp3", "wav", "aac", "m4a", "ogg"].includes(type)) return { tone: "audio", label: "Audio", badge: type.toUpperCase() };
  if (["zip", "rar", "7z", "tar", "gz"].includes(type)) return { tone: "archive", label: "Archive", badge: type.toUpperCase() };
  if (["js", "jsx", "ts", "tsx", "html", "css", "json", "py", "php"].includes(type)) return { tone: "code", label: "Code File", badge: type.toUpperCase() };
  return { tone: "generic", label: "File", badge: type.toUpperCase().slice(0, 5) || "FILE" };
};

const formatFileSize = (value) => {
  if (typeof value === "string" && Number.isNaN(Number(value))) {
    return value.trim() || "—";
  }

  const bytes = typeof value === "string" ? Number(value) : value;
  if (typeof bytes !== "number" || Number.isNaN(bytes) || bytes < 0) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toLocaleString(undefined, { maximumFractionDigits: 1 })} ${units[index]}`;
};

const formatDate = (date) => {
  if (!date || Number.isNaN(new Date(date).getTime())) return "—";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getCardFileUrl = (card) => card?.content?.imageUrl?.[0]?.url || "";

const getCardFileSize = (card) => {
  const mediaItems = card?.content?.imageUrl;
  const media = Array.isArray(mediaItems) ? mediaItems[0] : mediaItems;
  const values = [
    card?.sizeInBytes,
    card?.fileSize,
    card?.size,
    card?.content?.sizeInBytes,
    card?.content?.fileSize,
    card?.content?.size,
    media?.sizeInBytes,
    media?.fileSize,
    media?.size,
    media?.metaData?.sizeInBytes,
    media?.metaData?.fileSize,
    media?.metaData?.size,
    media?.metadata?.sizeInBytes,
    media?.metadata?.fileSize,
    media?.metadata?.size,
  ];

  return values.find((value) => value !== undefined && value !== null && value !== "");
};

const isEditorEmpty = (content) => {
  if (!content || content === "<p><br></p>") return true;
  const text = content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  return text === "" && !content.includes("<img");
};

const getTagNames = (tags) => (Array.isArray(tags) ? tags : [])
  .map((tag) => (typeof tag === "string" ? tag : tag?.name))
  .filter(Boolean);

const getErrorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const FileTypeIcon = ({ fileName, size = "large" }) => {
  const style = getFileStyle(getExtension(fileName));
  return (
    <span className={`file-detail-type-icon file-detail-type-icon--${style.tone} file-detail-type-icon--${size}`} aria-label={style.label}>
      <svg viewBox="0 0 40 52" fill="none" aria-hidden="true">
        <path className="file-detail-type-icon__paper" d="M4 4C4 1.79086 5.79086 0 8 0H26L36 10V48C36 50.2091 34.2091 52 32 52H8C5.79086 52 4 50.2091 4 48V4Z" stroke="currentColor" strokeWidth="2" />
        <path d="M26 0V6C26 8.20914 27.7909 10 30 10H36" stroke="currentColor" strokeWidth="2" />
        <line x1="10" y1="22" x2="30" y2="22" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
        <line x1="10" y1="28" x2="26" y2="28" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
        <line x1="10" y1="34" x2="28" y2="34" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
      </svg>
      <span>{style.badge}</span>
    </span>
  );
};

function FileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [buckets, setBuckets] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [isNotesFocused, setIsNotesFocused] = useState(false);
  const [isNotesEmpty, setIsNotesEmpty] = useState(true);
  const [isNoteImageUploading, setIsNoteImageUploading] = useState(false);
  const [showNotesToolbar, setShowNotesToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [showColorInput, setShowColorInput] = useState(false);
  const [colorValue, setColorValue] = useState("");
  const [newTag, setNewTag] = useState("");
  const [isTagMenuOpen, setIsTagMenuOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [destinationMode, setDestinationMode] = useState("");
  const [destinationBucketId, setDestinationBucketId] = useState("");
  const [destinationQuery, setDestinationQuery] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);

  const quillRef = useRef(null);
  const notesContainerRef = useRef(null);
  const noteImageUploadCountRef = useRef(0);
  const shouldSaveNotesAfterImageUploadRef = useRef(false);

  const fileName = file?.title || "Untitled file";
  const extension = getExtension(fileName);
  const fileStyle = getFileStyle(extension);
  const fileUrl = getCardFileUrl(file);
  const fileSize = getCardFileSize(file);
  const isImage = imageExtensions.includes(extension);
  const isVideo = videoExtensions.includes(extension) || file?.content?.imageUrl?.[0]?.urlType?.toLowerCase() === "video";
  const tags = getTagNames(file?.tags);
  const suggestedTags = tagOptions.filter((tag) =>
    tag?.name?.toLowerCase().includes(newTag.trim().toLowerCase()) && !tags.includes(tag?.name)
  );
  const destinationBuckets = useMemo(() => {
    const query = destinationQuery.trim().toLowerCase();
    if (!query) return buckets;
    return buckets.filter((bucket) => (
      bucket?.name?.toLowerCase().includes(query) ||
      bucket?.subBuckets?.some((subBucket) => subBucket?.name?.toLowerCase().includes(query))
    ));
  }, [buckets, destinationQuery]);

  const fetchFile = useCallback(async ({ showLoader = true } = {}) => {
    const token = localStorage.getItem("token");
    if (showLoader) setIsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}cards/single-card/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const card = response?.data?.data;
      setFile(card);
      setEditedTitle(card?.title || "");
      const noteContent = card?.note || "";
      setNotes(noteContent);
      setIsNotesEmpty(isEditorEmpty(noteContent));
    } catch (error) {
      console.error("Unable to load file:", error);
      if (error?.response?.data?.statusCode === 404) {
        navigate("/notFound");
      } else {
        toastify("error", getErrorMessage(error, "Unable to load the file."));
      }
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const fetchSupportingData = async () => {
      if (!token) return;
      try {
        const [bucketResponse, tagResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API}buckets/user-buckets`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${process.env.REACT_APP_API}tags/list`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setBuckets(bucketResponse?.data?.data?.bucketList || []);
        setTagOptions(tagResponse?.data?.data || []);
      } catch (error) {
        console.error("Unable to load file controls:", error);
      }
    };

    fetchFile();
    fetchSupportingData();
  }, [id, fetchFile]);

  const updateFile = async (payload, successMessage, onSuccess) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId: id, ...payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (successMessage) toastify("success", response?.data?.message || successMessage);
      onSuccess?.(response);
      return true;
    } catch (error) {
      console.error("Unable to update file:", error);
      toastify("error", getErrorMessage(error, "Unable to update file."));
      return false;
    }
  };

  const saveTitle = async () => {
    const title = editedTitle.trim();
    if (!title) {
      toastify("error", "File name cannot be empty.");
      return;
    }

    const previousTitle = file?.title;
    setFile((current) => ({ ...current, title }));
    const updated = await updateFile({ title }, "File renamed.", () => setIsEditingTitle(false));
    if (!updated) setFile((current) => ({ ...current, title: previousTitle }));
  };

  const toggleStar = async () => {
    const starred = !file?.starred;
    setFile((current) => ({ ...current, starred }));
    const updated = await updateFile({ starred }, "File updated.");
    if (!updated) setFile((current) => ({ ...current, starred: !starred }));
  };

  const saveNotes = useCallback(async () => {
    if (noteImageUploadCountRef.current > 0) {
      shouldSaveNotesAfterImageUploadRef.current = true;
      return;
    }

    const content = quillRef.current
      ? quillRef.current.getEditor().root.innerHTML
      : notes;
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId: id, note: content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes(content);
      setFile((current) => ({ ...current, note: content }));
      toastify("success", "Notes saved successfully");
    } catch (error) {
      console.error("Failed to save notes:", error);
      toastify("error", "Failed to save notes");
    }
  }, [id, notes]);

  const uploadAndInsertNoteImage = async (fileToUpload) => {
    if (!fileToUpload?.type?.startsWith("image/")) return;

    noteImageUploadCountRef.current += 1;
    setIsNoteImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("type", "other");

      const response = await services.uploadImage(formData, false);
      const uploadedAsset = response?.data?.asset || response?.asset || response?.data || response;
      const uploadedImageUrl = uploadedAsset?.url || response?.data?.url || response?.url;
      if (!uploadedImageUrl) throw new Error("The image upload response did not include a URL.");

      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection() || { index: quill.getLength() };
        quill.insertEmbed(range.index, "image", uploadedImageUrl);
        if (quill.hasFocus()) quill.setSelection(range.index + 1, 0, "silent");
        setIsNotesEmpty(false);
      }
    } catch (error) {
      console.error("Failed to upload note image:", error);
      toastify("error", "Failed to upload image. Please try again.");
    } finally {
      noteImageUploadCountRef.current -= 1;
      if (noteImageUploadCountRef.current === 0) {
        setIsNoteImageUploading(false);
        if (shouldSaveNotesAfterImageUploadRef.current) {
          shouldSaveNotesAfterImageUploadRef.current = false;
          saveNotes();
        }
      }
    }
  };

  const handleNotesPaste = (event) => {
    const items = (event.clipboardData || window.clipboardData)?.items || [];
    for (let index = 0; index < items.length; index += 1) {
      if (items[index].type.includes("image")) {
        event.preventDefault();
        uploadAndInsertNoteImage(items[index].getAsFile());
        return;
      }
    }
  };

  const handleNotesDrop = (event) => {
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile?.type?.startsWith("image/")) {
      event.preventDefault();
      uploadAndInsertNoteImage(droppedFile);
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && selection.toString().trim()) {
      const rect = selection.getRangeAt(0).getBoundingClientRect();
      setToolbarPosition({ top: rect.top - 72, left: rect.left + rect.width / 2 - 150 });
      setShowNotesToolbar(true);
    } else {
      setShowNotesToolbar(false);
      setShowLinkInput(false);
      setShowColorInput(false);
    }
  };

  const formatText = (command, value = null) => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    editor.focus();
    const mapping = {
      bold: "bold",
      italic: "italic",
      underline: "underline",
      strikeThrough: "strike",
      insertUnorderedList: "list",
      createLink: "link",
      foreColor: "color",
    };
    const format = mapping[command] || command;

    if (command === "insertUnorderedList") {
      const currentFormat = editor.getFormat();
      editor.format("list", currentFormat.list === "bullet" ? false : "bullet");
    } else if (command === "createLink") {
      if (value) editor.format("link", value);
    } else {
      const currentFormat = editor.getFormat();
      editor.format(format, currentFormat[format] ? false : value || true);
    }
    handleTextSelection();
  };

  const applyLink = () => {
    if (!linkUrl) return;
    formatText("createLink", linkUrl);
    setLinkUrl("");
    setShowLinkInput(false);
    setShowNotesToolbar(false);
  };

  const applyColor = () => {
    if (!colorValue) return;
    formatText("foreColor", colorValue);
    setColorValue("");
    setShowColorInput(false);
    setShowNotesToolbar(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notesContainerRef.current && !notesContainerRef.current.contains(event.target) && isNotesFocused) {
        setIsNotesFocused(false);
        setShowNotesToolbar(false);
        if (noteImageUploadCountRef.current > 0) {
          shouldSaveNotesAfterImageUploadRef.current = true;
        } else {
          saveNotes();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isNotesFocused, saveNotes]);

  const saveTags = async (nextTags) => {
    const tagPayload = nextTags.map((name) => ({ name }));
    const previousTags = file?.tags || [];
    setFile((current) => ({ ...current, tags: tagPayload }));
    const updated = await updateFile({ tags: tagPayload }, "Tags updated.");
    if (!updated) setFile((current) => ({ ...current, tags: previousTags }));
  };

  const addTag = () => {
    const tag = newTag.trim();
    if (!tag || tags.includes(tag)) {
      setNewTag("");
      setIsTagMenuOpen(false);
      return;
    }
    saveTags([...tags, tag]);
    setNewTag("");
    setIsTagMenuOpen(false);
  };

  const chooseSuggestedTag = (tag) => {
    if (!tag || tags.includes(tag)) return;
    saveTags([...tags, tag]);
    setNewTag("");
    setIsTagMenuOpen(false);
  };

  const removeTag = (tag) => saveTags(tags.filter((currentTag) => currentTag !== tag));

  const downloadFile = async () => {
    if (!fileUrl) {
      toastify("error", "This file does not have a downloadable URL.");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}cards/download-file`,
        { fileUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const base64Data = response?.data?.data?.fileBase64;
      if (!base64Data) throw new Error("No file contents returned.");

      const bytes = Uint8Array.from(atob(base64Data), (character) => character.charCodeAt(0));
      const blob = new Blob([bytes]);
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      toastify("success", "File downloaded successfully.");
    } catch (error) {
      console.error("Unable to download file:", error);
      toastify("error", getErrorMessage(error, "Unable to download file."));
    }
  };

  const copyPublicLink = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}cards/public-url`,
        { itemId: id, isPublic: true, itemType: "Card" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const publicUrl = response?.data?.data;
      if (!publicUrl) throw new Error("No public URL returned.");

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicUrl);
      } else {
        const input = document.createElement("input");
        input.value = publicUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      toastify("success", response?.data?.message || "Public link copied.");
    } catch (error) {
      console.error("Unable to create public link:", error);
      toastify("error", getErrorMessage(error, "Unable to copy the public link."));
    }
  };

  const openDestination = (mode) => {
    setDestinationMode(mode);
    setDestinationBucketId("");
    setDestinationQuery("");
  };

  const selectDestination = (bucket) => {
    setDestinationBucketId(bucket?._id);
    setDestinationQuery(bucket?.name || "");
  };

  const updateDestination = async () => {
    if (!destinationBucketId) {
      toastify("error", "Select a destination bucket first.");
      return;
    }

    const optionType = destinationMode === "move" ? "MoveTo" : "CopyTo";
    const updated = await updateFile(
      { optionType, destinationBucketID: destinationBucketId },
      destinationMode === "move" ? "File moved." : "File copied."
    );
    if (updated) {
      setDestinationMode("");
      if (destinationMode === "move") fetchFile({ showLoader: false });
    }
  };

  const deleteFile = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API}cards/delete`, {
        data: { deletedIds: [id] },
        headers: { Authorization: `Bearer ${token}` },
      });
      toastify("success", response?.data?.message || "File deleted.");
      navigate("/app/files", { replace: true });
    } catch (error) {
      console.error("Unable to delete file:", error);
      toastify("error", getErrorMessage(error, "Unable to delete file."));
    }
  };

  const getPreview = () => {
    if (fileUrl && isImage) {
      return <img src={fileUrl} alt={fileName} className="file-detail-preview__media" />;
    }
    if (fileUrl && isVideo) {
      return (
        <div className="file-detail-preview__video">
          <video src={fileUrl} controls={isPlaying} autoPlay={isPlaying} muted playsInline preload="metadata" />
          {!isPlaying && (
            <button type="button" onClick={() => setIsPlaying(true)} aria-label="Play video">
              <Play size={24} fill="currentColor" />
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="file-detail-preview__fallback">
        <FileTypeIcon fileName={fileName} size="extra-large" />
        <div>
          <p>{fileStyle.label}</p>
          <span>No preview available</span>
        </div>
        <button type="button" className="file-detail-button file-detail-button--primary" onClick={downloadFile}>
          <Download size={16} /> Download to view
        </button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="App-loading">
        <div className="App-logo" title="Zimzee" />
      </div>
    );
  }

  return (
    <section className="file-detail-page">
      <ToastContainer />

      <header className="file-detail-page__header">
        <div className="file-detail-breadcrumb">
          <button type="button" className="file-detail-icon-button" onClick={() => navigate("/app/files")} aria-label="Back to files">
            <ChevronLeft size={20} />
          </button>
          <button type="button" onClick={() => navigate("/app/files")}>Files</button>
          <span>/</span>
          <strong title={fileName}>{fileName}</strong>
        </div>
      </header>

      <main className="file-detail-layout">
        <section className="file-detail-preview-panel">
          <div className="file-detail-preview">{getPreview()}</div>
        </section>

        <aside className="file-detail-sidebar">
          <section className="file-detail-heading">
            {isEditingTitle ? (
              <div className="file-detail-title-editor">
                <input value={editedTitle} onChange={(event) => setEditedTitle(event.target.value)} autoFocus aria-label="File name" />
                <button type="button" className="file-detail-icon-button file-detail-icon-button--primary" onClick={saveTitle} aria-label="Save file name"><Check size={17} /></button>
                <button type="button" className="file-detail-icon-button" onClick={() => { setEditedTitle(fileName); setIsEditingTitle(false); }} aria-label="Cancel rename"><X size={17} /></button>
              </div>
            ) : (
              <div className="file-detail-title-row">
                <FileTypeIcon fileName={fileName} size="medium" />
                <div>
                  <div>
                    <h1 title={fileName}>{fileName}</h1>
                    <button type="button" className="file-detail-icon-button" onClick={() => setIsEditingTitle(true)} aria-label="Rename file"><Pencil size={16} /></button>
                    <button type="button" className={`file-detail-star ${file?.starred ? "is-starred" : ""}`} onClick={toggleStar} aria-label={file?.starred ? "Remove star" : "Star file"}>
                      <Star size={17} fill={file?.starred ? "currentColor" : "none"} />
                    </button>
                  </div>
                  <p>{fileStyle.label} · {formatFileSize(fileSize)}</p>
                </div>
              </div>
            )}
          </section>

          <section className="file-detail-actions">
            <button type="button" className="file-detail-button file-detail-button--primary" onClick={downloadFile}><Download size={16} /> Download</button>
            <button type="button" className="file-detail-button file-detail-button--outline" onClick={() => setShareOpen(true)}><Share2 size={16} /> Share</button>
            <button type="button" className="file-detail-button file-detail-button--outline" onClick={() => setShareOpen(true)}><Users size={16} /> Invite</button>
            <Menu as="div" className="file-detail-action-menu">
              <Menu.Button className="file-detail-icon-button file-detail-icon-button--outlined" aria-label="More file actions"><MoreHorizontal size={18} /></Menu.Button>
              <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95">
                <Menu.Items className="file-detail-action-menu__items">
                  <Menu.Item>{({ active }) => <button className={active ? "is-active" : ""} onClick={copyPublicLink}><LinkIcon size={16} /> Copy link</button>}</Menu.Item>
                  <Menu.Item>{({ active }) => <button className={active ? "is-active" : ""} onClick={() => openDestination("move")}><FolderInput size={16} /> Move to</button>}</Menu.Item>
                  <Menu.Item>{({ active }) => <button className={active ? "is-active" : ""} onClick={() => openDestination("copy")}><Copy size={16} /> Copy to</button>}</Menu.Item>
                  <Menu.Item>{({ active }) => <button className={`file-detail-action-menu__danger ${active ? "is-active" : ""}`} onClick={() => setIsDeleteOpen(true)}><Trash2 size={16} /> Delete</button>}</Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </section>

          <section className="file-detail-section file-detail-section--details">
            <h2>Details</h2>
            <dl>
              <div><dt><HardDrive size={16} /> Size</dt><dd>{formatFileSize(fileSize)}</dd></div>
              <div><dt><Calendar size={16} /> Created</dt><dd>{formatDate(file?.createdAt)}</dd></div>
              <div><dt><Calendar size={16} /> Modified</dt><dd>{formatDate(file?.updatedAt || file?.createdAt)}</dd></div>
              <div><dt><Folder size={16} /> Bucket</dt><dd>{file?.bucketId?.name || "No bucket"}</dd></div>
            </dl>
          </section>

          <section className="file-detail-section file-detail-section--notes">
            <div className="file-detail-section__heading"><h2>Notes</h2></div>
            <div className="file-detail-notes" ref={notesContainerRef}>
              {isNotesEmpty && !isNotesFocused && (
                <div className="file-detail-notes-placeholder">
                  <div>Click here to start typing…</div>
                  <div>Highlight text to format — click outside to save.</div>
                  <div>You can also drag or paste images right into this space.</div>
                </div>
              )}

              {isNoteImageUploading && (
                <div className="file-detail-notes-uploading"><span className="file-detail-notes-spinner" /> Uploading image…</div>
              )}

              <div
                onMouseUp={handleTextSelection}
                onClick={(event) => {
                  if (event.target.closest("a")) return;
                  if (!isNotesFocused) {
                    setIsNotesFocused(true);
                    quillRef.current?.getEditor().focus();
                  }
                }}
                onPaste={handleNotesPaste}
                onDrop={handleNotesDrop}
                onDragOver={(event) => event.preventDefault()}
              >
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={notes}
                  onChange={(content, delta, source, editor) => {
                    setNotes(content);
                    setIsNotesEmpty(
                      !content ||
                        content === "<p><br></p>" ||
                        (editor.getText().trim() === "" && !content.includes("<img"))
                    );
                  }}
                  modules={{ toolbar: false, clipboard: { matchVisual: false } }}
                  onFocus={() => setIsNotesFocused(true)}
                  className="file-detail-notes-editor"
                />
              </div>

              {showNotesToolbar && (
                <div className="file-detail-notes-toolbar" style={{ top: `${toolbarPosition.top}px`, left: `${toolbarPosition.left}px` }}>
                  {!showLinkInput && !showColorInput && (
                    <div className="file-detail-notes-toolbar-actions">
                      <button type="button" onClick={() => formatText("bold")} title="Bold"><strong>B</strong></button>
                      <button type="button" onClick={() => formatText("italic")} title="Italic"><em>I</em></button>
                      <button type="button" onClick={() => formatText("underline")} title="Underline"><span className="file-detail-notes-underlined">U</span></button>
                      <button type="button" onClick={() => formatText("strikeThrough")} title="Strikethrough"><span className="file-detail-notes-strikethrough">S</span></button>
                      <span className="file-detail-notes-toolbar-divider" />
                      <button type="button" onClick={() => formatText("insertUnorderedList")} title="Bullet list"><List size={15} /></button>
                      <span className="file-detail-notes-toolbar-divider" />
                      <button type="button" onClick={() => setShowLinkInput(true)} title="Add link"><LinkIcon size={15} /></button>
                      <button type="button" onClick={() => setShowColorInput(true)} title="Text color"><span className="file-detail-notes-color-icon" /></button>
                    </div>
                  )}

                  {showLinkInput && (
                    <div className="file-detail-notes-toolbar-input">
                      <input
                        type="url"
                        placeholder="Enter URL..."
                        value={linkUrl}
                        onChange={(event) => setLinkUrl(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            applyLink();
                          } else if (event.key === "Escape") {
                            setShowLinkInput(false);
                            setLinkUrl("");
                          }
                        }}
                        autoFocus
                      />
                      <button type="button" className="apply" onClick={applyLink}>Apply</button>
                      <button type="button" onClick={() => { setShowLinkInput(false); setLinkUrl(""); }} aria-label="Cancel"><X size={15} /></button>
                    </div>
                  )}

                  {showColorInput && (
                    <div className="file-detail-notes-toolbar-input">
                      <input
                        type="text"
                        placeholder="e.g. #ff0000, red"
                        value={colorValue}
                        onChange={(event) => setColorValue(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            applyColor();
                          } else if (event.key === "Escape") {
                            setShowColorInput(false);
                            setColorValue("");
                          }
                        }}
                        autoFocus
                      />
                      <button type="button" className="apply" onClick={applyColor}>Apply</button>
                      <button type="button" onClick={() => { setShowColorInput(false); setColorValue(""); }} aria-label="Cancel"><X size={15} /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="file-detail-section file-detail-section--tags">
            <h2>Tags</h2>
            <div className="file-detail-tags">
              {tags.map((tag) => (
                <span key={tag}>{tag}<button type="button" onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}><X size={13} /></button></span>
              ))}
            </div>
            <div className="file-detail-tag-input">
              <Tag size={16} />
              <input
                value={newTag}
                onChange={(event) => { setNewTag(event.target.value); setIsTagMenuOpen(true); }}
                onFocus={() => setIsTagMenuOpen(true)}
                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addTag(); } }}
                placeholder="Add a tag..."
                aria-label="Add a tag"
              />
              <button type="button" onClick={addTag}>Add</button>
              {isTagMenuOpen && suggestedTags.length > 0 && (
                <div className="file-detail-tag-menu">
                  {suggestedTags.map((tag) => <button type="button" key={tag?._id} onMouseDown={(event) => { event.preventDefault(); chooseSuggestedTag(tag.name); }}>{tag?.name}</button>)}
                </div>
              )}
            </div>
            <p>Press Enter or click Add to create a tag.</p>
          </section>
        </aside>
      </main>

      {shareOpen && <ShareCard shareBucketId={id} closeSharePop={() => setShareOpen(false)} cardsRefresh={() => fetchFile({ showLoader: false })} />}

      <Transition appear show={Boolean(destinationMode)} as={Fragment}>
        <Dialog as="div" className="file-detail-dialog" onClose={() => setDestinationMode("")}>
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"><div className="file-detail-dialog__backdrop" /></Transition.Child>
          <div className="file-detail-dialog__position">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="file-detail-dialog__panel">
                <Dialog.Title>{destinationMode === "move" ? "Move file to" : "Copy file to"}</Dialog.Title>
                <p>Choose a destination bucket.</p>
                <div className="file-detail-bucket-picker">
                  <div><input value={destinationQuery} onChange={(event) => { setDestinationQuery(event.target.value); setDestinationBucketId(""); }} placeholder="Search buckets" autoFocus /><ChevronDown size={16} /></div>
                  <ul>
                    {destinationBuckets.map((bucket) => (
                      <Fragment key={bucket?._id}>
                        <li><button type="button" className={destinationBucketId === bucket?._id ? "is-selected" : ""} onClick={() => selectDestination(bucket)}>{bucket?.name}</button></li>
                        {bucket?.subBuckets?.map((subBucket) => <li key={subBucket?._id}><button type="button" className={`file-detail-bucket-picker__subitem ${destinationBucketId === subBucket?._id ? "is-selected" : ""}`} onClick={() => selectDestination(subBucket)}>{subBucket?.name}</button></li>)}
                      </Fragment>
                    ))}
                    {!destinationBuckets.length && <li><span>No buckets found.</span></li>}
                  </ul>
                </div>
                <footer><button type="button" className="file-detail-button file-detail-button--outline" onClick={() => setDestinationMode("")}>Cancel</button><button type="button" className="file-detail-button file-detail-button--primary" onClick={updateDestination}>{destinationMode === "move" ? "Move" : "Copy"}</button></footer>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={isDeleteOpen} as={Fragment}>
        <Dialog as="div" className="file-detail-dialog" onClose={() => setIsDeleteOpen(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0 scale-95"><div className="file-detail-dialog__backdrop" /></Transition.Child>
          <div className="file-detail-dialog__position"><Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"><Dialog.Panel className="file-detail-dialog__panel file-detail-dialog__panel--delete"><Trash2 size={23} /><Dialog.Title>Delete file?</Dialog.Title><p>This action cannot be undone.</p><footer><button type="button" className="file-detail-button file-detail-button--outline" onClick={() => setIsDeleteOpen(false)}>Cancel</button><button type="button" className="file-detail-button file-detail-button--danger" onClick={deleteFile}>Delete</button></footer></Dialog.Panel></Transition.Child></div>
        </Dialog>
      </Transition>
    </section>
  );
}

export default FileDetail;
